/**
 * Pal Care — IV Fruits + Pal Surgery Table.
 *
 * Three services in one page:
 *   1. Talent Fruits (Life / Power / Stout) — buy with skulls to raise
 *      a pal's HP / ATK / DEF talent in +10 steps, capped at 100.
 *   2. Random Surgery — wipe all unlocked passives, roll a fresh set.
 *   3. Targeted Swap — replace one passive with any other from the
 *      library (excluding the pal's locked ones).
 *
 * Locked passives (guaranteed table, e.g. Cattiva → Coward, Jetragon
 * → Legend + Divine Dragon, Lucky pals → Lucky) are never touched.
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Currency } from "@/components/Atoms";
import { PalPortrait } from "@/components/PalAssets";
import {
  Apple, Beef, Carrot, Sparkles, Sword, ShieldCheck, Heart,
  Search, Wand2, RefreshCcw, ArrowRight, Lock,
} from "lucide-react";

const FRUITS = [
  { id: "life",  label: "Life Fruit",  Icon: Apple,  colour: "text-emerald-300", target: "HP" },
  { id: "power", label: "Power Fruit", Icon: Beef,   colour: "text-rose-300",    target: "ATK" },
  { id: "stout", label: "Stout Fruit", Icon: Carrot, colour: "text-sky-300",     target: "DEF" },
];

export default function PalCare() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [tab, setTab] = useState("fruit");
  const [pals, setPals] = useState([]);
  const [palsLoading, setPalsLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [prices, setPrices] = useState({ fruit_price: 1500, surgery_random: 5000, surgery_swap: 15000, surgery_add: 10000, fruit_step: 10, talent_cap: 100 });

  // Pull the work-pals payload — it already has the talents, passives,
  // is_lucky, level info we need; no need for a second endpoint.
  const [fruitInv, setFruitInv] = useState({ life: 0, power: 0, stout: 0 });
  const refreshPals = () =>
    api.get("/economy/work/pals").then(({ data }) => {
      const arr = (data.pals || []).map((p) => ({ ...p }));
      Promise.all(arr.map((p) =>
        api.get(`/palgame/box/${p.pal_name}`).then(({ data: d }) => ({
          ...p,
          talents: d.ivs,
          full_passives: d.passives || [],
        })).catch(() => p)
      )).then((withTalents) => {
        setPals(withTalents);
        setPicked((cur) => withTalents.find((x) => x.pal_name === cur?.pal_name) || withTalents[0] || null);
      });
    }).catch(() => {}).finally(() => setPalsLoading(false));

  const refreshInventory = () =>
    api.get("/palgame/care/inventory").then(({ data }) => {
      setFruitInv(data.fruits || { life: 0, power: 0, stout: 0 });
    }).catch(() => {});

  useEffect(() => {
    refreshPals();
    refreshInventory();
    api.get("/palgame/care/prices").then(({ data }) => setPrices(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="palcare-tab" className="fade-up">
      {/* Tab strip */}
      <div className="flex gap-2 border-b border-white/10 mb-6 flex-wrap">
        {[
          ["fruit",   "Talent Fruits",   Apple],
          ["random",  "Random Surgery",  Wand2],
          ["add",     "Add Passive",     Sparkles],
          ["swap",    "Targeted Swap",   ArrowRight],
        ].map(([id, label, Icon]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm uppercase tracking-wider border-b-2 transition-colors flex items-center gap-2 ${
              tab === id
                ? "border-[var(--teal)] text-[var(--teal)]"
                : "border-transparent text-[var(--muted)] hover:text-[var(--bone)]"
            }`}
            data-testid={`palcare-tab-${id}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* LEFT — Pal picker */}
        <aside className="lg:col-span-3 space-y-3">
          <PalPicker
            pals={pals} loading={palsLoading}
            value={picked?.pal_name} onChange={(name) => setPicked(pals.find((p) => p.pal_name === name) || null)}
          />
        </aside>

        {/* RIGHT — Active tab */}
        <main className="lg:col-span-9">
          {!picked ? (
            <div className="glass-card p-12 text-center text-[var(--muted)] text-sm">
              <Sparkles size={24} className="mx-auto mb-3 opacity-60" />
              Pick a pal on the left to start.
            </div>
          ) : tab === "fruit" ? (
            <FruitsTab pal={picked} prices={prices} inventory={fruitInv}
                       refresh={() => { refreshPals(); refreshInventory(); refreshProfile?.(); }} />
          ) : tab === "random" ? (
            <RandomSurgeryTab pal={picked} prices={prices} refresh={() => { refreshPals(); refreshProfile?.(); }} />
          ) : tab === "add" ? (
            <AddPassiveTab pal={picked} prices={prices} refresh={() => { refreshPals(); refreshProfile?.(); }} />
          ) : (
            <SwapSurgeryTab pal={picked} prices={prices} refresh={() => { refreshPals(); refreshProfile?.(); }} />
          )}
        </main>
      </div>
    </div>
  );
}

// ── Pal picker (vertical list) ────────────────────────────────────────
function PalPicker({ pals, loading, value, onChange }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return pals;
    return pals.filter((p) => p.pal_name.toLowerCase().includes(n));
  }, [pals, q]);
  return (
    <div className="glass-card p-3" data-testid="palcare-pal-picker">
      <h3 className="label mb-2">Your Pals</h3>
      <div className="relative mb-2">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input
          className="input pl-8 text-sm"
          placeholder={loading ? "Loading…" : "Search…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={loading}
        />
      </div>
      <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
        {filtered.length === 0 && !loading && (
          <p className="text-xs text-[var(--muted)] italic text-center py-3">No pals yet.</p>
        )}
        {filtered.map((p) => {
          const active = value === p.pal_name;
          return (
            <button
              key={p.pal_name}
              type="button"
              onClick={() => onChange(p.pal_name)}
              className={`w-full text-left p-2 rounded flex items-center gap-2 transition-colors ${
                active
                  ? "bg-[rgba(0,229,255,0.10)] ring-1 ring-[var(--teal)]/40"
                  : "hover:bg-white/5"
              } ${p.is_lucky ? "ring-1 ring-yellow-400/40" : ""}`}
              data-testid={`palcare-pal-${p.pal_name}`}
            >
              <PalPortrait name={p.pal_name} size="w-10 h-10" />
              <div className="flex-1 min-w-0">
                <div className="text-xs truncate flex items-center gap-1">
                  {p.is_lucky && <span className="text-yellow-300 text-[10px]">✨</span>}
                  {p.pal_name}
                </div>
                <div className="text-[10px] text-[var(--muted)]">Lv {p.level}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Fruits tab ────────────────────────────────────────────────────────
function FruitsTab({ pal, prices, inventory, refresh }) {
  const toast = useToast();
  const [fruit, setFruit] = useState("life");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const talentCap = prices.talent_cap;
  const step = prices.fruit_step;
  const tMap = { life: pal.talents?.HP, power: pal.talents?.Attack, stout: pal.talents?.Defense };
  const current = tMap[fruit] ?? 0;
  const needToMax = Math.max(0, Math.ceil((talentCap - current) / step));
  const willApply = Math.min(qty, Math.max(1, needToMax));
  const cost = willApply * (prices.fruit_price || 0);
  const stored = inventory?.[fruit] ?? 0;
  const canUseStored = stored >= willApply && needToMax > 0;

  const feed = async ({ useInventory }) => {
    setBusy(true);
    try {
      const { data } = await api.post("/palgame/care/fruit", {
        pal_name: pal.pal_name, fruit, quantity: qty, use_inventory: !!useInventory,
      });
      toast.success(
        useInventory
          ? `+${data.applied * step} talent · used ${data.applied} stored ${fruit.charAt(0).toUpperCase() + fruit.slice(1)} Fruit`
          : `+${data.applied * step} talent · ${data.talent_before} → ${data.talent_after}`
      );
      refresh?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="glass-card p-6">
        <h3 className="label mb-3">Current Talents</h3>
        <div className="grid grid-cols-3 gap-3" data-testid="palcare-current-talents">
          <TalentTile Icon={Heart}       label="HP"      value={pal.talents?.HP ?? 0}        colour="text-emerald-300" cap={talentCap} />
          <TalentTile Icon={Sword}       label="Attack"  value={pal.talents?.Attack ?? 0}    colour="text-rose-300"    cap={talentCap} />
          <TalentTile Icon={ShieldCheck} label="Defense" value={pal.talents?.Defense ?? 0}   colour="text-sky-300"     cap={talentCap} />
        </div>
      </div>

      {/* Stored fruit inventory — awarded as drops from battles, towers,
          arena, PvP and expeditions. Players can use these without
          spending a single skull. */}
      <div className="glass-card p-6" data-testid="palcare-fruit-inventory">
        <h3 className="label mb-3">Stored Fruits <span className="text-[10px] text-[var(--muted)] ml-1">(drop rewards)</span></h3>
        <div className="grid grid-cols-3 gap-3">
          {FRUITS.map((f) => (
            <div key={f.id}
                 className={`p-3 rounded-md border ${
                   inventory?.[f.id]
                     ? "border-emerald-400/30 bg-emerald-500/5"
                     : "border-white/10 bg-black/20"
                 }`}>
              <div className="flex items-center gap-2">
                <f.Icon size={18} className={f.colour} />
                <div className="flex-1">
                  <div className="text-xs font-medium">{f.label}</div>
                  <div className="text-[10px] text-[var(--muted)]">+{step} {f.target}</div>
                </div>
                <span className={`text-lg font-mono ${
                  inventory?.[f.id] ? "text-emerald-300" : "text-[var(--muted)]"
                }`}
                      data-testid={`palcare-fruit-stored-${f.id}`}>
                  {inventory?.[f.id] ?? 0}
                </span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[var(--muted)] mt-3 italic">
          Win battles, towers, arena fights, PvP, or expeditions to earn more — odds scale with difficulty.
        </p>
      </div>

      <div className="glass-card p-6">
        <h3 className="label mb-3">Pick a fruit</h3>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          {FRUITS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFruit(f.id)}
              className={`p-4 rounded-lg border text-left transition-colors relative ${
                fruit === f.id
                  ? "border-[var(--teal)] bg-[rgba(0,229,255,0.10)]"
                  : "border-white/10 hover:border-white/30"
              }`}
              data-testid={`palcare-fruit-${f.id}`}
            >
              <f.Icon size={28} className={`${f.colour} mb-2`} />
              <div className="text-sm font-medium">{f.label}</div>
              <div className="text-[10px] text-[var(--muted)] mt-1">+{step} {f.target} talent</div>
              <div className="text-[10px] text-[var(--muted)] mt-1 font-mono">
                <Currency value={prices.fruit_price} /> each
              </div>
              {inventory?.[f.id] > 0 && (
                <span className="absolute top-1.5 right-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-900/60 border border-emerald-400/40 text-emerald-200 font-mono">
                  {inventory[f.id]}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs uppercase tracking-wider text-[var(--muted)]">Quantity</label>
          <input
            type="number"
            min={1} max={20}
            value={qty}
            onChange={(e) => setQty(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
            className="input w-20 text-center"
            data-testid="palcare-fruit-qty"
          />
          <span className="text-xs text-[var(--muted)]">
            (Auto-caps at {needToMax} for this stat — won't overshoot 100)
          </span>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            type="button"
            disabled={busy || needToMax === 0 || !canUseStored}
            onClick={() => feed({ useInventory: true })}
            className="btn btn-primary"
            data-testid="palcare-fruit-use-stored"
            title={canUseStored ? "Use stored fruits — no skulls spent" : `You only have ${stored} stored`}
          >
            {needToMax === 0
              ? "Talent already at 100"
              : `Use ${willApply} stored · 0 skulls (${stored} owned)`}
          </button>
          <button
            type="button"
            disabled={busy || needToMax === 0}
            onClick={() => feed({ useInventory: false })}
            className="btn btn-secondary"
            data-testid="palcare-fruit-buy"
          >
            {needToMax === 0
              ? "Talent already at 100"
              : `Buy ${willApply} for ${cost.toLocaleString()} skulls`}
          </button>
        </div>
      </div>
    </div>
  );
}

function TalentTile({ Icon, label, value, colour, cap }) {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div className="bg-black/30 rounded-md p-3 text-center" data-testid={`palcare-talent-${label.toLowerCase()}`}>
      <Icon size={16} className={`mx-auto mb-1 ${colour}`} />
      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{label}</div>
      <div className={`text-2xl font-mono ${colour}`}>{value}</div>
      <div className="text-[10px] text-[var(--muted)] mt-1">/ {cap}</div>
      <div className="h-1.5 bg-white/5 rounded overflow-hidden mt-2">
        <div className="h-full" style={{ width: `${pct}%`, background: "currentColor" }} />
      </div>
    </div>
  );
}

// ── Random surgery tab ────────────────────────────────────────────────
function RandomSurgeryTab({ pal, prices, refresh }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [lastRoll, setLastRoll] = useState(null);

  const roll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/palgame/care/surgery/random", { pal_name: pal.pal_name });
      setLastRoll(data);
      toast.success(`Surgery complete — new passives rolled.`);
      refresh?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="glass-card p-6 space-y-4">
      <h3 className="label">Random Surgery</h3>
      <p className="text-sm text-[var(--muted)]">
        Pays the surgeon to wipe and reroll <em>every passive on this pal</em>{" "}
        (locked passives stay). The new set is rolled with a luckier curve than wild catches.
      </p>
      <p className="text-sm">
        <span className="text-[var(--muted)]">Current passives:</span>{" "}
        {(pal.full_passives || []).length === 0
          ? <span className="italic text-[var(--muted)]">none</span>
          : pal.full_passives.map((p) => (
              <PassivePill key={p.name} passive={p} />
            ))
        }
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={roll}
        className="btn btn-primary"
        data-testid="palcare-random-roll"
      >
        <RefreshCcw size={14} className="mr-2" />
        Re-roll all passives · {prices.surgery_random?.toLocaleString()} skulls
      </button>
      {lastRoll && (
        <div className="mt-3 p-3 bg-black/30 rounded border border-white/10 text-sm" data-testid="palcare-random-result">
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1">Result</div>
          <div className="text-xs text-[var(--muted)] mb-1">
            Before: {lastRoll.old_passives.join(", ") || "—"}
          </div>
          <div>
            After: <span className="text-[var(--teal)]">{lastRoll.new_passives.join(", ") || "—"}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add passive tab ───────────────────────────────────────────────────
// Fills an empty slot with a chosen passive — no swap, nothing destroyed.
// Backed by POST /palgame/care/surgery/add. Pricing is cheaper than swap
// (default 10k vs 15k) since the player loses nothing in the trade.
function AddPassiveTab({ pal, prices, refresh }) {
  const toast = useToast();
  const [library, setLibrary] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState(null);
  const [defaultPrices] = useState({ surgery_add: 10000 });

  useEffect(() => {
    api.get("/skills/builder/passive").then(({ data }) => setLibrary(data.skills || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setNewName(null); setQ("");
  }, [pal.pal_name]);

  const slotsUsed = (pal.full_passives || []).length;
  const slotsFree = Math.max(0, 4 - slotsUsed);
  const isFull = slotsFree === 0;

  const filteredLib = useMemo(() => {
    const n = q.trim().toLowerCase();
    const equipped = new Set((pal.full_passives || []).map((p) => p.name));
    const base = library.filter((s) => !equipped.has(s.name));
    if (!n) return base.slice(0, 100);
    return base.filter((s) => (s.name || "").toLowerCase().includes(n)
                            || (s.description || "").toLowerCase().includes(n)).slice(0, 80);
  }, [library, q, pal.full_passives]);

  const add = async () => {
    if (!newName) return;
    setBusy(true);
    try {
      const { data } = await api.post("/palgame/care/surgery/add", {
        pal_name: pal.pal_name,
        new_passive: newName,
      });
      toast.success(`Added ${data.new_passive}`);
      setNewName(null);
      refresh?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const price = prices.surgery_add ?? defaultPrices.surgery_add;

  return (
    <div className="grid md:grid-cols-3 gap-4">
      {/* LEFT: slot summary */}
      <div className="glass-card p-4 md:col-span-1" data-testid="palcare-add-slots">
        <h3 className="label mb-3">Passive slots</h3>
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => {
            const filled = i < slotsUsed;
            const p = filled ? pal.full_passives[i] : null;
            return (
              <div
                key={i}
                className={`p-2 rounded border flex items-center gap-2 text-sm ${
                  filled
                    ? "border-white/15 bg-white/5"
                    : "border-dashed border-[var(--teal)]/40 bg-[rgba(0,229,255,0.04)]"
                }`}
              >
                <span className={`w-5 text-center text-[10px] font-mono ${filled ? "text-[var(--muted)]" : "text-[var(--teal)]"}`}>{i + 1}</span>
                <span className={`flex-1 truncate ${filled ? "" : "italic text-[var(--muted)]"}`}>
                  {filled ? p.name : "empty"}
                </span>
              </div>
            );
          })}
        </div>
        <p className={`text-[10px] mt-3 ${isFull ? "text-[var(--warning)]" : "text-[var(--muted)]"}`}>
          {isFull
            ? "All 4 slots filled. Use the Targeted Swap tab to replace one."
            : `${slotsFree} empty slot${slotsFree === 1 ? "" : "s"} available.`}
        </p>
      </div>

      {/* RIGHT: passive picker */}
      <div className="glass-card p-4 md:col-span-2">
        <h3 className="label mb-3">Pick a passive to add</h3>
        <div className="relative mb-2">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            className="input pl-8 text-sm"
            placeholder={`Search ${library.length} passives…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={isFull}
            data-testid="palcare-add-search"
          />
        </div>
        <div className="max-h-96 overflow-y-auto pr-1 space-y-1">
          {filteredLib.map((s) => {
            const active = newName === s.name;
            return (
              <button
                key={s.name}
                type="button"
                disabled={isFull}
                onClick={() => !isFull && setNewName(s.name)}
                className={`w-full text-left p-2 rounded text-sm transition-colors flex items-center gap-2 ${
                  isFull
                    ? "opacity-40 cursor-not-allowed"
                    : active
                      ? "bg-[rgba(0,229,255,0.10)] ring-1 ring-[var(--teal)]/40"
                      : "hover:bg-white/5"
                }`}
                data-testid={`palcare-add-new-${(s.asset || s.name).replace(/[^a-z0-9]/gi, "_")}`}
              >
                <PassiveRankBadge rank={s.rank} icon={s.icon} />
                <span className="flex-1 truncate">{s.name}</span>
              </button>
            );
          })}
          {filteredLib.length === 0 && (
            <p className="text-xs text-[var(--muted)] italic text-center py-4">No matches</p>
          )}
        </div>
      </div>

      {/* Confirm bar */}
      <div className="md:col-span-3 glass-card p-4 flex items-center justify-between gap-3">
        <div className="text-sm">
          {newName
            ? <>Add <span className="text-emerald-300">{newName}</span> to a free slot.</>
            : <span className="text-[var(--muted)] italic">Pick a passive to enable add.</span>}
        </div>
        <button
          type="button"
          disabled={busy || !newName || isFull}
          onClick={add}
          className="btn btn-primary"
          data-testid="palcare-add-confirm"
        >
          Add · {price.toLocaleString()} skulls
        </button>
      </div>
    </div>
  );
}

// ── Targeted swap tab ─────────────────────────────────────────────────
function SwapSurgeryTab({ pal, prices, refresh }) {
  const toast = useToast();
  const [oldName, setOldName] = useState(null);
  const [library, setLibrary] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState(null);

  useEffect(() => {
    api.get("/skills/builder/passive").then(({ data }) => setLibrary(data.skills || [])).catch(() => {});
  }, []);

  useEffect(() => {
    // Reset selections when the pal switches
    setOldName(null); setNewName(null); setQ("");
  }, [pal.pal_name]);

  const filteredLib = useMemo(() => {
    const n = q.trim().toLowerCase();
    const equipped = new Set((pal.full_passives || []).map((p) => p.name));
    const base = library.filter((s) => !equipped.has(s.name));
    if (!n) return base.slice(0, 100);
    return base.filter((s) => (s.name || "").toLowerCase().includes(n)
                            || (s.description || "").toLowerCase().includes(n)).slice(0, 80);
  }, [library, q, pal.full_passives]);

  const swap = async () => {
    if (!oldName || !newName) return;
    setBusy(true);
    try {
      const { data } = await api.post("/palgame/care/surgery/swap", {
        pal_name: pal.pal_name,
        old_passive: oldName,
        new_passive: newName,
      });
      toast.success(`Swapped ${data.old_passive} → ${data.new_passive}`);
      setOldName(null); setNewName(null);
      refresh?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {/* Old passive picker */}
      <div className="glass-card p-4">
        <h3 className="label mb-3">1. Pick a passive to remove</h3>
        {(pal.full_passives || []).length === 0 ? (
          <p className="text-xs text-[var(--muted)] italic">
            No passives equipped — nothing to swap.
          </p>
        ) : (
          <div className="space-y-2">
            {pal.full_passives.map((p) => {
              const isLocked = isPassiveLocked(p, pal);
              const active = oldName === p.name;
              return (
                <button
                  key={p.name}
                  type="button"
                  disabled={isLocked}
                  onClick={() => !isLocked && setOldName(p.name)}
                  className={`w-full text-left p-2 rounded border transition-colors flex items-center gap-2 ${
                    isLocked
                      ? "border-white/5 bg-black/40 text-white/30 cursor-not-allowed"
                      : active
                        ? "border-[var(--teal)] bg-[rgba(0,229,255,0.10)]"
                        : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`palcare-swap-old-${p.name}`}
                >
                  {isLocked && <Lock size={12} className="text-white/30" />}
                  <span className="text-sm flex-1 truncate">{p.name}</span>
                  {isLocked && <span className="text-[10px] uppercase tracking-wider">locked</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* New passive picker */}
      <div className="glass-card p-4">
        <h3 className="label mb-3">2. Pick its replacement</h3>
        <div className="relative mb-2">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            className="input pl-8 text-sm"
            placeholder={`Search ${library.length} passives…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="palcare-swap-search"
          />
        </div>
        <div className="max-h-96 overflow-y-auto pr-1 space-y-1">
          {filteredLib.map((s) => {
            const active = newName === s.name;
            return (
              <button
                key={s.name}
                type="button"
                onClick={() => setNewName(s.name)}
                className={`w-full text-left p-2 rounded text-sm transition-colors flex items-center gap-2 ${
                  active
                    ? "bg-[rgba(0,229,255,0.10)] ring-1 ring-[var(--teal)]/40"
                    : "hover:bg-white/5"
                }`}
                data-testid={`palcare-swap-new-${(s.asset || s.name).replace(/[^a-z0-9]/gi, "_")}`}
              >
                <PassiveRankBadge rank={s.rank} icon={s.icon} />
                <span className="flex-1 truncate">{s.name}</span>
              </button>
            );
          })}
          {filteredLib.length === 0 && (
            <p className="text-xs text-[var(--muted)] italic text-center py-4">No matches</p>
          )}
        </div>
      </div>

      <div className="md:col-span-2 glass-card p-4 flex items-center justify-between gap-3">
        <div className="text-sm">
          {oldName && newName
            ? <>Swap <span className="text-red-300">{oldName}</span> → <span className="text-emerald-300">{newName}</span></>
            : <span className="text-[var(--muted)] italic">Pick both sides to enable swap.</span>}
        </div>
        <button
          type="button"
          disabled={busy || !oldName || !newName}
          onClick={swap}
          className="btn btn-primary"
          data-testid="palcare-swap-confirm"
        >
          Swap · {prices.surgery_swap?.toLocaleString()} skulls
        </button>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────
function isPassiveLocked(passive, pal) {
  // Lucky pals always retain "Lucky". Anything in the guaranteed list
  // for this species is also locked.
  if (pal.is_lucky && passive.name === "Lucky") return true;
  // We don't ship the guaranteed table to the frontend — the backend
  // is the source of truth — so we just optimistically allow any swap
  // and let the server reject locked ones with a 400.
  return false;
}

function PassivePill({ passive }) {
  return (
    <span
      key={passive.name}
      className="inline-flex items-center gap-1 mr-1.5 mb-1 text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 uppercase tracking-wider"
    >
      {passive.name}
    </span>
  );
}

function PassiveRankBadge({ rank, icon }) {
  if (rank === null || rank === undefined) {
    return <span className="w-4 h-4 inline-flex items-center justify-center rounded bg-white/5 text-[var(--muted)] text-[10px]">·</span>;
  }
  if (icon) {
    const url = icon.startsWith("http") ? icon : `${process.env.REACT_APP_BACKEND_URL}${icon}`;
    return <img src={url} alt="" className="w-4 h-4 object-contain" loading="lazy" />;
  }
  return <span className="w-4 h-4 inline-flex items-center justify-center rounded bg-white/5 text-[10px] font-mono">{rank}</span>;
}
